import { PracticeType } from '@/constants/onboardingConstants'
import { TAX_ENTITY_TYPES } from '@/features/taxes/taxConstants'
import type { FinancialProfile } from '@/reducers/auth/userReducer'
import { getFinancialProfile } from '@/selectors/user.selectors'
import { useCallback, useEffect } from 'react'

import { useFetchResponse, useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  readChooserDecision,
  recordChooserDecision,
  useChooserDecision,
} from './chooserDecision'
import { fetchSignupPlans } from './signupPlans.actions'
import { orderedCards } from './signupPlans'
import type { SignupPlan } from './signupPlans.types'
import { useNewCatalogSignup } from './useNewCatalogSignup'

/** S corps, C corps and group practices skip the plan chooser under today's rule. */
export const isChooserBypassCohort = (
  profile: Pick<FinancialProfile, 'taxEntityType' | 'practiceType'> | undefined
): boolean =>
  profile?.taxEntityType === TAX_ENTITY_TYPES.form_1120_s ||
  profile?.taxEntityType === TAX_ENTITY_TYPES.form_1120 ||
  profile?.practiceType === PracticeType.group

/**
 * One rule for every page that asks: a bypass cohort still reaches the chooser when the
 * catalog is on and two or more plans other than Lite come back, since Lite never shows to it.
 */
export const showsChooser = ({
  bypassCohort,
  catalog,
  plans,
}: {
  bypassCohort: boolean
  catalog: boolean
  plans: SignupPlan[] | undefined
}): boolean =>
  !bypassCohort ||
  (catalog && orderedCards(plans ?? [], { excludeLite: true }).length >= 2)

/** Module-level so its identity is stable across renders. */
const skipPlans = () => () => Promise.resolve(undefined)

/**
 * Whether the chooser step is part of this visitor's signup, for the progress header. With the
 * catalog on, the routing already recorded for this profile wins; otherwise the plan list decides,
 * and its answer is recorded so a later slow or failed request cannot flip the route.
 */
export const useChooserShown = (): boolean => {
  const profile = useReselector(getFinancialProfile)
  const bypassCohort = isChooserBypassCohort(profile)
  const catalogFlag = useNewCatalogSignup()
  const catalog = catalogFlag === true
  const recorded = useChooserDecision(profile)
  // A recorded routing was made under an enabled flag, so it holds while the flag re-resolves.
  const decided = catalogFlag !== false && bypassCohort ? recorded : null
  const plans = useFetchResponse(
    catalog && bypassCohort && decided === null ? fetchSignupPlans : skipPlans
  )
  const shown = decided ?? showsChooser({ bypassCohort, catalog, plans })

  useEffect(() => {
    if (catalog && bypassCohort && decided === null && plans) {
      recordChooserDecision(profile, shown)
    }
  }, [catalog, bypassCohort, decided, plans, profile, shown])

  return shown
}

/**
 * For Back handlers: answers from the recorded routing, else waits for the plan list instead of
 * reading a request that may still be pending. A failed request keeps today's routing, unrecorded.
 */
export const useResolveChooserShown = (): (() => Promise<boolean>) => {
  const dispatch = useAppDispatch()
  const profile = useReselector(getFinancialProfile)
  const bypassCohort = isChooserBypassCohort(profile)
  const catalogFlag = useNewCatalogSignup()
  const catalog = catalogFlag === true

  return useCallback(async () => {
    if (!bypassCohort) return true
    if (catalogFlag === false) return false
    const recorded = readChooserDecision(profile)
    if (recorded !== null) return recorded
    if (!catalog) return false
    const plans = await fetchSignupPlans()(dispatch).catch(() => undefined)
    const shown = showsChooser({ bypassCohort, catalog, plans })
    if (plans) recordChooserDecision(profile, shown)
    return shown
  }, [bypassCohort, catalog, catalogFlag, dispatch, profile])
}
